import { useState } from 'react';
import { cn } from '@/lib/utils';

const STORAGE_KEY = 'oceanne-lang';
type Lang = 'fr' | 'en';

interface LanguageToggleProps {
  className?: string;
  onChange?: (lang: Lang) => void;
}

function readLang(): Lang {
  if (typeof window === 'undefined') return 'fr';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === 'en' ? 'en' : 'fr';
}

export function LanguageToggle({ className, onChange }: LanguageToggleProps) {
  const [lang, setLang] = useState<Lang>(() => readLang());

  const handleClick = (value: Lang) => {
    if (value === lang) return;
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, value);
      window.dispatchEvent(new CustomEvent('language-change', { detail: { lang: value } }));
    }
    onChange?.(value);
    setLang(value);
  };

  // Phase 27.11 — contrast bump v2 (Lighthouse AA). Active state uses text-foreground
  // + decoration-primary underline (primary indicates state visually via underline color,
  // text stays full slate ~12:1). Inactive : text-foreground/80 (~4.6:1).
  const btnClass = (active: boolean) =>
    cn(
      'px-2 py-1 text-sm font-bold uppercase tracking-widest transition-colors',
      active
        ? 'text-foreground underline underline-offset-4 decoration-primary decoration-2'
        : 'text-foreground/80 hover:text-foreground'
    );

  return (
    <div role="group" aria-label="Langue" className={cn('inline-flex items-center gap-1', className)}>
      <button
        type="button"
        aria-pressed={lang === 'fr'}
        onClick={() => handleClick('fr')}
        className={btnClass(lang === 'fr')}
      >
        FR
      </button>
      <span aria-hidden="true" className="text-foreground/60">|</span>
      <button
        type="button"
        aria-pressed={lang === 'en'}
        onClick={() => handleClick('en')}
        className={btnClass(lang === 'en')}
      >
        EN
      </button>
    </div>
  );
}
